import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  ArrowLeft, Package, DollarSign, AlertTriangle, Layers, Search, Laptop,
  ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import api from '../lib/api';

export default function LocationDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('item_name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    loadDashboard();
  }, [id]);

  const loadDashboard = async () => {
    try {
      const res = await api.get(`/locations/${id}/dashboard`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load location dashboard');
      navigate('/locations');
    } finally { setLoading(false); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  if (loading || !data) {
    return <div className="space-y-6"><div className="h-8 w-48 skeleton-pulse"></div><div className="h-96 skeleton-pulse rounded-lg"></div></div>;
  }

  const { location, summary, categories, stock_items, it_assets } = data;

  const filtered = stock_items.filter(s =>
    s.item_name.toLowerCase().includes(search.toLowerCase()) ||
    s.variant_sku.toLowerCase().includes(search.toLowerCase()) ||
    s.category.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let aV, bV;
    switch (sortKey) {
      case 'item_name': aV = a.item_name.toLowerCase(); bV = b.item_name.toLowerCase(); break;
      case 'variant_sku': aV = a.variant_sku; bV = b.variant_sku; break;
      case 'category': aV = a.category; bV = b.category; break;
      case 'quantity': aV = a.quantity; bV = b.quantity; break;
      case 'value': aV = a.value; bV = b.value; break;
      default: aV = a.item_name; bV = b.item_name;
    }
    if (aV < bV) return sortDir === 'asc' ? -1 : 1;
    if (aV > bV) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader = ({ label, colKey, className }) => (
    <TableHead className={`cursor-pointer select-none hover:bg-slate-100 transition-colors ${className || ''}`} onClick={() => toggleSort(colKey)}>
      <div className="flex items-center gap-1">
        {label}
        {sortKey === colKey ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </div>
    </TableHead>
  );

  const catEntries = Object.entries(categories).sort((a, b) => b[1].units - a[1].units);

  return (
    <div className="space-y-6" data-testid="location-dashboard-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/locations')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">{location.name}</h1>
            <p className="text-slate-500 mt-1">{location.code} &bull; {location.type} &bull; {location.address || 'No address'}</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Package className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold" data-testid="stat-units">{summary.total_units.toLocaleString()}</p><p className="text-sm text-slate-500">Total Units</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold text-emerald-600" data-testid="stat-value">{formatCurrency(summary.total_value)}</p><p className="text-sm text-slate-500">Total Value</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Layers className="w-5 h-5 text-purple-600" /></div>
          <div><p className="text-2xl font-bold text-purple-600" data-testid="stat-variants">{summary.total_variants}</p><p className="text-sm text-slate-500">SKUs</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${summary.low_stock_count > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
            <AlertTriangle className={`w-5 h-5 ${summary.low_stock_count > 0 ? 'text-red-600' : 'text-slate-400'}`} />
          </div>
          <div><p className={`text-2xl font-bold ${summary.low_stock_count > 0 ? 'text-red-600' : ''}`} data-testid="stat-low">{summary.low_stock_count}</p><p className="text-sm text-slate-500">Low Stock</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Laptop className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold text-amber-600" data-testid="stat-it">{summary.it_assets_count}</p><p className="text-sm text-slate-500">IT Assets</p></div>
        </CardContent></Card>
      </div>

      {/* Category Breakdown */}
      {catEntries.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Stock by Category</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {catEntries.map(([cat, d]) => (
                <div key={cat} className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium capitalize">{cat.replace('_', ' ')}</p>
                  <p className="text-lg font-bold">{d.units.toLocaleString()} <span className="text-sm font-normal text-slate-500">units</span></p>
                  <p className="text-sm text-emerald-600">{formatCurrency(d.value)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stock Detail Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div><CardTitle>Stock Detail</CardTitle><CardDescription>{sorted.length} items at this location</CardDescription></div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search items..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="loc-stock-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader label="Item" colKey="item_name" />
                <SortHeader label="SKU" colKey="variant_sku" />
                <SortHeader label="Category" colKey="category" />
                <TableHead>Size</TableHead>
                <SortHeader label="Qty" colKey="quantity" className="text-right" />
                <TableHead className="text-right">Min</TableHead>
                <SortHeader label="Value" colKey="value" className="text-right" />
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((s, i) => (
                <TableRow key={i} className="cursor-pointer" onClick={() => navigate(`/items/${s.item_id}`)}>
                  <TableCell className="font-medium">{s.item_name}</TableCell>
                  <TableCell className="font-mono text-sm">{s.variant_sku}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize text-xs">{s.category.replace('_', ' ')}</Badge></TableCell>
                  <TableCell>{s.size || '-'}</TableCell>
                  <TableCell className="text-right font-bold">{s.quantity}</TableCell>
                  <TableCell className="text-right text-slate-500">{s.min_stock || '-'}</TableCell>
                  <TableCell className="text-right">{formatCurrency(s.value)}</TableCell>
                  <TableCell>
                    {s.low_stock ? <Badge variant="destructive">Low</Badge> : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">OK</Badge>}
                  </TableCell>
                </TableRow>
              ))}
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500"><Package className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No stock at this location</p></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* IT Assets */}
      {it_assets.length > 0 && (
        <Card>
          <CardHeader><CardTitle>IT Assets at This Location ({it_assets.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Asset</TableHead><TableHead>Asset Tag</TableHead><TableHead>Serial #</TableHead><TableHead>Checked Out</TableHead></TableRow></TableHeader>
              <TableBody>
                {it_assets.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.item_name}</TableCell>
                    <TableCell>{a.asset_tag || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">{a.serial_number || '-'}</TableCell>
                    <TableCell>{a.checked_out_at ? a.checked_out_at.slice(0, 10) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
